import { useEffect, useState, useCallback } from "react";

import {
    collection,
    getDocs,
    deleteDoc,
    doc
} from "firebase/firestore";

import { db } from "../../firebase/firebase";

import Sidebar from "../Sidebar";
import AdminNavbar from "../AdminNavbar";
import Footer from "../../components/common/Footer";

import "../../assets/css/admin/contactList.css";


function ContactList() {

    const [contacts, setContacts] = useState([]);

    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const [selectedMessageId, setSelectedMessageId] = useState(null);

    const [loading, setLoading] = useState(true);

    const [popup, setPopup] = useState({
        show: false,
        type: "",
        message: ""
    });


    /* =========================================================
                        FILTER STATES
    ========================================================= */

    const [searchTerm, setSearchTerm] = useState("");

    const [eventMonth, setEventMonth] = useState("");

    const [requestMonth, setRequestMonth] = useState("");

    const [eventFromDate, setEventFromDate] = useState("");

    const [eventToDate, setEventToDate] = useState("");

    const [packageFilter, setPackageFilter] = useState("");


    /* =========================================================
                        LOAD CONTACTS
    ========================================================= */

    const loadContacts = useCallback(async () => {

        try {

            setLoading(true);

            const snapshot = await getDocs(
                collection(db, "contacts")
            );

            const list = [];

            snapshot.forEach((item) => {

                list.push({

                    id: item.id,

                    ...item.data()

                });

            });


            /* =====================================================
                            SORT NEWEST FIRST
            ===================================================== */

            list.sort((a, b) => {

                const dateA = a.createdAt?.toDate
                    ? a.createdAt.toDate()
                    : new Date(0);

                const dateB = b.createdAt?.toDate
                    ? b.createdAt.toDate()
                    : new Date(0);

                return dateB - dateA;

            });


            setContacts(list);

        }

        catch (error) {

            console.error(
                "Error loading contact messages:",
                error
            );

            showPopup(
                "error",
                "Failed to load contact messages."
            );

        }

        finally {

            setLoading(false);

        }

    }, []);


    /* =========================================================
                            INITIAL LOAD
    ========================================================= */

    useEffect(() => {

        loadContacts();

    }, [loadContacts]);


    /* =========================================================
                            POPUP
    ========================================================= */

    const showPopup = (type, message) => {

        setPopup({

            show: true,

            type,

            message

        });


        setTimeout(() => {

            setPopup({

                show: false,

                type: "",

                message: ""

            });

        }, 3000);

    };


    /* =========================================================
                        DELETE MESSAGE
    ========================================================= */

    const deleteMessage = (id) => {

        setSelectedMessageId(id);

        setShowDeleteModal(true);

    };


    /* =========================================================
                        CONFIRM DELETE
    ========================================================= */

    const confirmDeleteMessage = async () => {

        try {

            await deleteDoc(

                doc(

                    db,

                    "contacts",

                    selectedMessageId

                )

            );


            setShowDeleteModal(false);

            setSelectedMessageId(null);


            showPopup(

                "success",

                "Message deleted successfully."

            );


            loadContacts();

        }

        catch (error) {

            console.error(error);


            setShowDeleteModal(false);


            showPopup(

                "error",

                "Failed to delete message."

            );

        }

    };


    /* =========================================================
                        CLEAR FILTERS
    ========================================================= */

    const clearFilters = () => {

        setSearchTerm("");

        setEventMonth("");

        setRequestMonth("");

        setEventFromDate("");

        setEventToDate("");

        setPackageFilter("");

    };


    /* =========================================================
                        PACKAGE LIST
    ========================================================= */

    const packageOptions = [

        ...new Set(

            contacts

                .flatMap((item) => {

                    if (Array.isArray(item.selectedPackages)) {

                        return item.selectedPackages;

                    }

                    if (item.packageType) {

                        return [item.packageType];

                    }

                    return [];

                })

                .filter(Boolean)

        )

    ];


    /* =========================================================
                        MONTH LIST
    ========================================================= */

    const months = [

        {
            value: "01",
            label: "January"
        },

        {
            value: "02",
            label: "February"
        },

        {
            value: "03",
            label: "March"
        },

        {
            value: "04",
            label: "April"
        },

        {
            value: "05",
            label: "May"
        },

        {
            value: "06",
            label: "June"
        },

        {
            value: "07",
            label: "July"
        },

        {
            value: "08",
            label: "August"
        },

        {
            value: "09",
            label: "September"
        },

        {
            value: "10",
            label: "October"
        },

        {
            value: "11",
            label: "November"
        },

        {
            value: "12",
            label: "December"
        }

    ];


    /* =========================================================
                        FILTER LOGIC
    ========================================================= */

    const filteredContacts = contacts.filter((item) => {


        /* =====================================================
                            SEARCH
        ===================================================== */

        const search = searchTerm
            .trim()
            .toLowerCase();


        const matchesSearch =

            !search ||

            item.name
                ?.toLowerCase()
                .includes(search)

            ||

            item.email
                ?.toLowerCase()
                .includes(search)

            ||

            item.phone
                ?.toLowerCase()
                .includes(search);


        /* =====================================================
                        EVENT DATE
        ===================================================== */

        const eventDate = item.eventDate
            ? new Date(item.eventDate)
            : null;


        let matchesEventMonth = true;


        if (eventMonth && eventDate) {

            const month = String(

                eventDate.getMonth() + 1

            ).padStart(2, "0");


            matchesEventMonth =

                month === eventMonth;

        }


        /* =====================================================
                        EVENT DATE RANGE
        ===================================================== */

        let matchesFromDate = true;

        let matchesToDate = true;


        if (eventFromDate && eventDate) {

            const from = new Date(eventFromDate);

            from.setHours(0, 0, 0, 0);

            matchesFromDate = eventDate >= from;

        }


        if (eventToDate && eventDate) {

            const to = new Date(eventToDate);

            to.setHours(23, 59, 59, 999);

            matchesToDate = eventDate <= to;

        }


        /* =====================================================
                        REQUEST MONTH
        ===================================================== */

        let matchesRequestMonth = true;


        if (requestMonth && item.createdAt?.toDate) {

            const submittedDate =
                item.createdAt.toDate();


            const month = String(

                submittedDate.getMonth() + 1

            ).padStart(2, "0");


            matchesRequestMonth =

                month === requestMonth;

        }


        /* =====================================================
                        PACKAGE
        ===================================================== */

        let matchesPackage = true;


        if (packageFilter) {

            const selectedPackages =
                Array.isArray(item.selectedPackages)

                    ? item.selectedPackages

                    : item.packageType
                        ? [item.packageType]
                        : [];


            matchesPackage =

                selectedPackages.includes(

                    packageFilter

                );

        }


        return (

            matchesSearch &&

            matchesEventMonth &&

            matchesRequestMonth &&

            matchesFromDate &&

            matchesToDate &&

            matchesPackage

        );

    });


    /* =========================================================
                        LOADING
    ========================================================= */

    if (loading) {

        return (

            <div className="contact-loading">

                <h2>

                    Loading Contact Messages...

                </h2>

            </div>

        );

    }


    return (

        <>

            {/* =================================================
                            DELETE MODAL
            ================================================= */}

            {showDeleteModal && (

                <div className="delete-overlay">

                    <div className="delete-modal">

                        <h2>

                            Delete Message?

                        </h2>

                        <p>

                            Are you sure you want to delete
                            this contact message?

                            <br />

                            <br />

                            This action cannot be undone.

                        </p>


                        <div className="delete-actions">

                            <button

                                className="cancel-btn"

                                onClick={() => {

                                    setShowDeleteModal(false);

                                    setSelectedMessageId(null);

                                }}

                            >

                                Cancel

                            </button>


                            <button

                                className="confirm-btn"

                                onClick={confirmDeleteMessage}

                            >

                                Delete

                            </button>

                        </div>

                    </div>

                </div>

            )}


            {/* =================================================
                            POPUP
            ================================================= */}

            {popup.show && (

                <div
                    className={`luxury-popup ${popup.type}`}
                >

                    <div className="popup-icon">

                        {popup.type === "success"
                            ? "✓"
                            : "✕"}

                    </div>


                    <div className="popup-content">

                        <h3>

                            {popup.type === "success"
                                ? "Success"
                                : "Error"}

                        </h3>


                        <p>

                            {popup.message}

                        </p>

                    </div>

                </div>

            )}


            {/* =================================================
                            ADMIN LAYOUT
            ================================================= */}

            <div className="admin-layout">

                <AdminNavbar />


                <div className="admin-main">

                    <Sidebar />


                    <div className="contact-list-page">


                        {/* =================================================
                                    HEADER
                        ================================================= */}

                        <div className="contact-list-header">

                            <div>

                                <h1 className="contact-list-title">

                                    📩 Contact & Booking Requests

                                </h1>


                                <p className="contact-list-subtitle">

                                    Manage customer enquiries,
                                    event bookings and photography
                                    session requests.

                                </p>

                            </div>


                            <div className="contact-list-count">

                                <span>

                                    {filteredContacts.length}

                                </span>

                                <p>

                                    Showing Requests

                                </p>

                            </div>

                        </div>


                        {/* =================================================
                                    FILTER PANEL
                        ================================================= */}

                        <div className="booking-filter-panel">


                            <div className="filter-panel-heading">

                                <div>

                                    <h2>

                                        🔎 Request Filters

                                    </h2>

                                    <p>

                                        Find customer enquiries
                                        quickly.

                                    </p>

                                </div>


                                <button

                                    className="clear-filter-btn"

                                    onClick={clearFilters}

                                >

                                    ↻ Clear Filters

                                </button>

                            </div>


                            <div className="booking-filter-grid">


                                {/* SEARCH */}

                                <div className="filter-field">

                                    <label>

                                        Search Customer

                                    </label>

                                    <input

                                        type="text"

                                        placeholder="Name, email or phone..."

                                        value={searchTerm}

                                        onChange={(e) =>
                                            setSearchTerm(
                                                e.target.value
                                            )
                                        }

                                    />

                                </div>


                                {/* EVENT MONTH */}

                                <div className="filter-field">

                                    <label>

                                        Event Month

                                    </label>

                                    <select

                                        value={eventMonth}

                                        onChange={(e) =>
                                            setEventMonth(
                                                e.target.value
                                            )
                                        }

                                    >

                                        <option value="">

                                            All Event Months

                                        </option>


                                        {months.map((month) => (

                                            <option

                                                key={month.value}

                                                value={month.value}

                                            >

                                                {month.label}

                                            </option>

                                        ))}

                                    </select>

                                </div>


                                {/* REQUEST MONTH */}

                                <div className="filter-field">

                                    <label>

                                        Request Received

                                    </label>

                                    <select

                                        value={requestMonth}

                                        onChange={(e) =>
                                            setRequestMonth(
                                                e.target.value
                                            )
                                        }

                                    >

                                        <option value="">

                                            All Request Months

                                        </option>


                                        {months.map((month) => (

                                            <option

                                                key={month.value}

                                                value={month.value}

                                            >

                                                {month.label}

                                            </option>

                                        ))}

                                    </select>

                                </div>


                                {/* PACKAGE */}

                                <div className="filter-field">

                                    <label>

                                        Photography Package

                                    </label>

                                    <select

                                        value={packageFilter}

                                        onChange={(e) =>
                                            setPackageFilter(
                                                e.target.value
                                            )
                                        }

                                    >

                                        <option value="">

                                            All Packages

                                        </option>


                                        {packageOptions.map(
                                            (pkg) => (

                                                <option

                                                    key={pkg}

                                                    value={pkg}

                                                >

                                                    {pkg}

                                                </option>

                                            )
                                        )}

                                    </select>

                                </div>


                                {/* FROM DATE */}

                                <div className="filter-field">

                                    <label>

                                        Event From

                                    </label>

                                    <input

                                        type="date"

                                        value={eventFromDate}

                                        onChange={(e) =>
                                            setEventFromDate(
                                                e.target.value
                                            )
                                        }

                                    />

                                </div>


                                {/* TO DATE */}

                                <div className="filter-field">

                                    <label>

                                        Event To

                                    </label>

                                    <input

                                        type="date"

                                        value={eventToDate}

                                        onChange={(e) =>
                                            setEventToDate(
                                                e.target.value
                                            )
                                        }

                                    />

                                </div>

                            </div>


                            {/* FILTER RESULT */}

                            <div className="filter-result">

                                <span>

                                    Showing

                                    <strong>

                                        {" "}
                                        {filteredContacts.length}

                                    </strong>

                                    {" "}of{" "}

                                    <strong>

                                        {contacts.length}

                                    </strong>

                                    {" "}requests

                                </span>

                            </div>

                        </div>


                        {/* =================================================
                                    REQUEST LIST
                        ================================================= */}

                        {filteredContacts.length === 0 ? (

                            <div className="contact-list-empty">

                                <div className="empty-icon">

                                    🔍

                                </div>

                                <h2>

                                    No Requests Found

                                </h2>

                                <p>

                                    No customer requests match
                                    your current filters.

                                </p>


                                <button

                                    className="clear-filter-btn"

                                    onClick={clearFilters}

                                >

                                    Clear Filters

                                </button>

                            </div>

                        ) : (

                            <div className="contact-list-container">

                                {filteredContacts.map((item) => (

                                    <div

                                        key={item.id}

                                        className="contact-list-card"

                                    >


                                        {/* CARD HEADER */}

                                        <div className="contact-list-card-header">

                                            <div>

                                                <h2 className="contact-list-name">

                                                    {item.name}

                                                </h2>


                                                {item.eventType && (

                                                    <span className="contact-list-event-badge">

                                                        {item.eventType}

                                                    </span>

                                                )}

                                            </div>

                                        </div>


                                        {/* CARD BODY */}

                                        <div className="contact-list-body">


                                            <div className="contact-list-row">

                                                <span className="contact-list-label">

                                                    📧 Email

                                                </span>

                                                <span className="contact-list-value">

                                                    {item.email}

                                                </span>

                                            </div>


                                            <div className="contact-list-row">

                                                <span className="contact-list-label">

                                                    📱 Phone

                                                </span>

                                                <span className="contact-list-value">

                                                    {item.phone}

                                                </span>

                                            </div>


                                            <div className="contact-list-row">

                                                <span className="contact-list-label">

                                                    📦 Package

                                                </span>

                                                <span className="contact-list-value">

                                                    {Array.isArray(
                                                        item.selectedPackages
                                                    )

                                                        ? item.selectedPackages.join(
                                                            ", "
                                                        )

                                                        : item.packageType ||
                                                          "-"

                                                    }

                                                </span>

                                            </div>


                                            <div className="contact-list-row">

                                                <span className="contact-list-label">

                                                    📅 Event Date

                                                </span>

                                                <span className="contact-list-value">

                                                    {item.eventDate || "-"}

                                                </span>

                                            </div>


                                            <div className="contact-list-row">

                                                <span className="contact-list-label">

                                                    🕒 Submitted On

                                                </span>

                                                <span className="contact-list-value">

                                                    {item.createdAt?.toDate

                                                        ? item.createdAt
                                                            .toDate()
                                                            .toLocaleString(
                                                                "en-IN",
                                                                {
                                                                    dateStyle:
                                                                        "medium",

                                                                    timeStyle:
                                                                        "short"
                                                                }
                                                            )

                                                        : "-"}

                                                </span>

                                            </div>


                                            {/* CUSTOMER MESSAGE */}

                                            <div className="contact-list-message-box">

                                                <h3 className="contact-list-message-title">

                                                    💬 Customer Message

                                                </h3>


                                                <p className="contact-list-message">

                                                    {item.message ||
                                                        "No message provided."}

                                                </p>

                                            </div>

                                        </div>


                                        {/* CARD FOOTER */}

                                        <div className="contact-list-footer">

                                            <button

                                                className="contact-list-delete-btn"

                                                onClick={() =>
                                                    deleteMessage(
                                                        item.id
                                                    )
                                                }

                                            >

                                                🗑 Delete Message

                                            </button>

                                        </div>

                                    </div>

                                ))}

                            </div>

                        )}

                    </div>

                </div>

            </div>


            <Footer />

        </>

    );

}


export default ContactList;